import type {
  AdminAnalyticsResponse,
  AdminDashboardDrilldownResponse,
  AdminDashboardResponse,
  AdminReportExportFormat,
  AdminReportTransactionListResponse,
  DashboardDrilldownMetric,
  ReportCategory,
  ReportPaymentMethod,
  ReportSourceType,
} from '@mds/shared/types/reporting';

import { api } from '@/lib/api';

/** The dashboard and analytics endpoints — see `backend/docs/reporting.md`. */

/**
 * The filters `/analytics`, `/transactions` and `/analytics/export` share.
 *
 * `from` and `to` are inclusive Beirut calendar dates and the server caps the
 * span at 366 days. `eventId` cannot be combined with the booking-domain
 * filters, because event registrations hang off no facility, resource or
 * customer — the server rejects that pairing rather than quietly ignoring it.
 */
export type ReportFilterQuery = {
  from: string;
  to: string;
  facilityId?: string;
  resourceId?: string;
  customerId?: string;
  eventId?: string;
  category?: ReportCategory;
};

/** A type alias, so it carries the index signature the client's `query` needs. */
export type TransactionQuery = ReportFilterQuery & {
  page?: number;
  limit?: number;
  search?: string;
  method?: ReportPaymentMethod;
  sourceType?: ReportSourceType;
  sort?: 'recordedAt' | 'amountMinor' | 'reference' | 'customerName';
  direction?: 'asc' | 'desc';
};

/**
 * The thirteen figures behind the dashboard tiles, for one Beirut date.
 *
 * These were summed in the browser over a single page of bookings until this
 * endpoint existed, which is why the tiles used to be tagged "Provisional".
 */
export const getDashboard = (date: string): Promise<AdminDashboardResponse> =>
  api.get<AdminDashboardResponse>('admin/reports/dashboard', { query: { date } });

/** The rows behind one dashboard figure. The shape varies by metric. */
export const getDashboardDrilldown = (
  metric: DashboardDrilldownMetric,
  date: string,
  page = 1,
  limit = 25,
): Promise<AdminDashboardDrilldownResponse> =>
  api.get<AdminDashboardDrilldownResponse>('admin/reports/dashboard/drilldown', {
    query: { metric, date, page, limit },
  });

/**
 * Summary, category/space/package/event rollups and the monthly series.
 *
 * "Collected" is money actually recorded in the range — not booking value and
 * not projected income — so nothing here may be re-derived from a transaction
 * page.
 */
export const getAnalytics = (query: ReportFilterQuery): Promise<AdminAnalyticsResponse> =>
  api.get<AdminAnalyticsResponse>('admin/reports/analytics', { query });

/**
 * One page of the payment ledger. The `summary` on the response covers the
 * whole filtered result, not the page, and is the only total worth showing.
 */
export const listTransactions = (query: TransactionQuery) =>
  api.get<AdminReportTransactionListResponse>('admin/reports/transactions', { query });

/**
 * The same filtered ledger as a file.
 *
 * The server names the file and counts the rows, so both are read off the
 * response rather than guessed here. Above 50,000 transactions it refuses with
 * `REPORT_EXPORT_TOO_LARGE`, which surfaces as an ordinary ApiError.
 */
export async function exportAnalytics(
  query: ReportFilterQuery,
  format: AdminReportExportFormat,
): Promise<{ blob: Blob; filename: string; rowCount: number | null }> {
  const { blob, filename, headers } = await api.getBlob('admin/reports/analytics/export', {
    query: { ...query, format },
  });

  const counted = Number(headers.get('X-Report-Row-Count'));

  return {
    blob,
    filename: filename ?? `mds-analytics-${query.from}-to-${query.to}.${format}`,
    rowCount: Number.isFinite(counted) ? counted : null,
  };
}
